import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Calendar,
  Clock,
  RefreshCw,
  ExternalLink,
  ShieldAlert,
  Infinity as InfinityIcon,
  Ban,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  adminApi,
  type AdminInvoice,
  type AdminSubscriptionView,
} from '@/services/adminApi';

const PLAN_OPTIONS = [
  'freemium_trial',
  'starter',
  'growth',
  'professional',
  'agency_starter',
  'agency_professional',
];

/**
 * AdminSubscriptionDetailPage — single company billing deep dive.
 *
 * Reads via /admin/customers/:cid/subscription which stitches Firestore
 * billing+trial with Autumn customer snapshot (invoices, upcoming,
 * payment method, MRR/ARR/lifetime totals).
 *
 * Write actions (cancel/uncancel/extend-trial/change-plan/refresh/grant
 * unlimited/reset-counters) are dialog-driven with reason field
 * required by the BE `requireReason` middleware.
 */
export default function AdminSubscriptionDetailPage() {
  const { companyId = '' } = useParams<{ companyId: string }>();
  const [data, setData] = useState<AdminSubscriptionView | null>(null);
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Action open state
  const [pendingExtendTrial, setPendingExtendTrial] = useState(false);
  const [pendingChangePlan, setPendingChangePlan] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);
  const [pendingUncancel, setPendingUncancel] = useState(false);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [pendingUnlimited, setPendingUnlimited] = useState(false);
  const [pendingResetCounters, setPendingResetCounters] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [detail, invs] = await Promise.all([
          adminApi.subscription.detail(companyId),
          adminApi.subscription.invoices(companyId, 50).catch(() => ({ items: [], count: 0, customerId: companyId })),
        ]);
        if (cancelled) return;
        setData(detail);
        setInvoices(invs.items);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  const openPortal = async () => {
    try {
      const r = await adminApi.subscription.portal(companyId, window.location.href);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to={`/admin/customers/${companyId}`}><ArrowLeft className="h-4 w-4" /> Back to customer</Link>
        </Button>
      </div>

      {/* Header KPIs */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            Subscription
            <Badge variant="outline">{data.planId}</Badge>
            <PlanStatusBadge status={data.autumn?.status ?? 'none'} cancelAtPeriodEnd={data.autumn?.cancelAtPeriodEnd ?? false} />
          </h1>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            {companyId}
            {data.autumn?.customerId && data.autumn.customerId !== companyId && (
              <> · billed via <Link to={`/admin/customers/${data.autumn.customerId}`} className="hover:underline">{data.autumn.customerId.slice(0, 12)}…</Link></>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPendingRefresh(true)}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh from Autumn
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={openPortal}>
            <ExternalLink className="h-3.5 w-3.5" /> Stripe portal
          </Button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="MRR" value={fmtMoney(data.autumn?.mrrAmount ?? 0, data.autumn?.currency)} />
        <KpiTile label="ARR" value={fmtMoney(data.autumn?.arrAmount ?? 0, data.autumn?.currency)} />
        <KpiTile label="Total paid lifetime" value={fmtMoney(data.autumn?.totalPaidLifetime ?? 0, data.autumn?.currency)} />
        <KpiTile
          label="Next payment"
          value={data.autumn?.nextPaymentAt ? fmtDate(data.autumn.nextPaymentAt) : '—'}
          sub={data.autumn?.nextPaymentAmount != null ? fmtMoney(data.autumn.nextPaymentAmount, data.autumn.currency) : undefined}
        />
      </div>

      {/* Trial + payment method */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" /> Trial
            </h2>
            {data.trial ? (
              <>
                <Row k="Status" v={data.trial.status} />
                <Row k="Started" v={fmtDate(data.trial.startedAt)} />
                <Row k="Ends" v={fmtDate(data.trial.endsAt)} />
                {data.trial.convertedAt && <Row k="Converted" v={fmtDate(data.trial.convertedAt)} />}
                {data.trial.daysLeft != null && <Row k="Days left" v={data.trial.daysLeft.toString()} />}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No trial document.</p>
            )}
            <div className="pt-2">
              <Button size="sm" variant="outline" onClick={() => setPendingExtendTrial(true)}>
                <Calendar className="h-3.5 w-3.5 mr-1.5" /> Extend trial
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-5 space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Payment method
            </h2>
            {data.autumn?.paymentMethod ? (
              <>
                <Row k="Brand" v={data.autumn.paymentMethod.brand ?? '—'} />
                <Row k="Last 4" v={data.autumn.paymentMethod.last4 ? `•••• ${data.autumn.paymentMethod.last4}` : '—'} />
                <Row k="Expiry" v={
                  data.autumn.paymentMethod.expMonth && data.autumn.paymentMethod.expYear
                    ? `${String(data.autumn.paymentMethod.expMonth).padStart(2, '0')}/${data.autumn.paymentMethod.expYear}`
                    : '—'
                } />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No payment method on file.</p>
            )}
            <Row k="Period start" v={fmtDate(data.autumn?.currentPeriodStart)} />
            <Row k="Period end" v={fmtDate(data.autumn?.currentPeriodEnd)} />
          </CardContent>
        </Card>
      </div>

      {/* Admin actions grid */}
      <Card className="border-border/50">
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Admin actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPendingChangePlan(true)}>
              <CreditCard className="h-3.5 w-3.5" /> Change plan
            </Button>
            {data.autumn?.cancelAtPeriodEnd ? (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPendingUncancel(true)}>
                <RotateCcw className="h-3.5 w-3.5" /> Uncancel
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => setPendingCancel(true)}>
                <Ban className="h-3.5 w-3.5" /> Cancel
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPendingUnlimited(true)}>
              <InfinityIcon className="h-3.5 w-3.5" /> Grant unlimited
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPendingResetCounters(true)}>
              <RotateCcw className="h-3.5 w-3.5" /> Reset counters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Invoice history */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-border/50">
            <h2 className="text-sm font-semibold">Invoices</h2>
          </div>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">No invoices found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left font-medium text-muted-foreground py-3 px-4">Created</th>
                    <th className="text-left font-medium text-muted-foreground py-3 px-3">Status</th>
                    <th className="text-left font-medium text-muted-foreground py-3 px-3">Number</th>
                    <th className="text-right font-medium text-muted-foreground py-3 px-3">Paid</th>
                    <th className="text-right font-medium text-muted-foreground py-3 px-3">Total</th>
                    <th className="text-right font-medium text-muted-foreground py-3 px-4">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-border/30 last:border-0 hover:bg-accent/40">
                      <td className="py-2 px-4 text-xs text-muted-foreground">{fmtDate(inv.created)}</td>
                      <td className="py-2 px-3"><InvoiceStatusBadge status={inv.status} /></td>
                      <td className="py-2 px-3 text-xs font-mono">{inv.number ?? '—'}</td>
                      <td className="py-2 px-3 text-right">{fmtMoney(inv.amountPaidDecimal, inv.currency)}</td>
                      <td className="py-2 px-3 text-right">{fmtMoney(inv.amountDueDecimal, inv.currency)}</td>
                      <td className="py-2 px-4 text-right">
                        {inv.hostedInvoiceUrl ? (
                          <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                            View <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Dialogs ──────────────────────────────────────────────────────── */}
      <ExtendTrialDialog
        open={pendingExtendTrial}
        onOpenChange={setPendingExtendTrial}
        companyId={companyId}
        currentEndsAt={data.trial?.endsAt}
        onDone={refresh}
      />
      <ChangePlanDialog
        open={pendingChangePlan}
        onOpenChange={setPendingChangePlan}
        companyId={companyId}
        currentPlanId={data.planId}
        onDone={refresh}
      />
      <CancelSubscriptionDialog
        open={pendingCancel}
        onOpenChange={setPendingCancel}
        companyId={companyId}
        onDone={refresh}
      />
      <ReasonConfirmDialog
        open={pendingUncancel}
        onOpenChange={setPendingUncancel}
        title="Uncancel subscription?"
        description="This removes the cancellation flag. The subscription will renew normally at the end of the current period."
        confirmLabel="Uncancel"
        onConfirm={async (reason) => {
          await adminApi.subscription.uncancel(companyId, reason);
          toast.success('Subscription uncancelled');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingRefresh}
        onOpenChange={setPendingRefresh}
        title="Refresh from Autumn?"
        description="Forces a re-sync from Autumn. Useful when Firestore and Autumn drifted (e.g. after a manual Autumn dashboard edit)."
        confirmLabel="Refresh"
        onConfirm={async (reason) => {
          await adminApi.subscription.refresh(companyId, reason);
          toast.success('Refreshed from Autumn');
          refresh();
        }}
      />
      <ReasonConfirmDialog
        open={pendingUnlimited}
        onOpenChange={setPendingUnlimited}
        title="Grant the unlimited ops plan?"
        description="Promotes the company to the internal `unlimited` plan, bypasses Autumn billing checks, and clears the trial document. Use only for staff/founder/investor accounts."
        confirmLabel="Grant unlimited"
        destructive
        onConfirm={async (reason) => {
          await adminApi.subscription.grantUnlimited(companyId, reason);
          toast.success('Granted unlimited');
          refresh();
        }}
      />
      <ResetCountersDialog
        open={pendingResetCounters}
        onOpenChange={setPendingResetCounters}
        companyId={companyId}
        onDone={refresh}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-muted-foreground w-32 shrink-0">{k}</span>
      <span className="font-medium break-all">{v}</span>
    </div>
  );
}

function KpiTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function PlanStatusBadge({ status, cancelAtPeriodEnd }: { status: string; cancelAtPeriodEnd: boolean }) {
  if (cancelAtPeriodEnd) return <Badge variant="destructive">Cancels at period end</Badge>;
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    active: 'default',
    trialing: 'secondary',
    past_due: 'destructive',
    canceled: 'destructive',
    incomplete: 'outline',
    incomplete_expired: 'outline',
    unpaid: 'destructive',
    none: 'outline',
  };
  return <Badge variant={variants[status] ?? 'outline'}>{status}</Badge>;
}

function InvoiceStatusBadge({ status }: { status: AdminInvoice['status'] }) {
  const variants: Record<AdminInvoice['status'], 'default' | 'secondary' | 'destructive' | 'outline'> = {
    paid: 'default',
    open: 'secondary',
    draft: 'outline',
    void: 'outline',
    uncollectible: 'destructive',
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB');
}

function fmtMoney(amount: number, currency = 'eur'): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
}

function ExtendTrialDialog({
  open, onOpenChange, companyId, currentEndsAt, onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  companyId: string;
  currentEndsAt?: string;
  onDone: () => void;
}) {
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setDays(7); setReason(''); } }, [open]);

  const submit = async () => {
    if (!reason.trim() || days <= 0 || busy) return;
    setBusy(true);
    try {
      const r = await adminApi.subscription.extendTrial(companyId, days, reason.trim());
      toast.success(`Trial extended by ${r.addedDays} days`, {
        description: `New end: ${new Date(r.newEndsAt).toLocaleDateString('en-GB')}`,
      });
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend trial</DialogTitle>
          <DialogDescription>
            Current trial.endsAt: {fmtDate(currentEndsAt)}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ext-days">Days to add</Label>
            <Input id="ext-days" type="number" min={1} max={90} value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ext-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="ext-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason.trim() || days <= 0}>{busy ? 'Saving...' : 'Extend'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangePlanDialog({
  open, onOpenChange, companyId, currentPlanId, onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  companyId: string;
  currentPlanId: string;
  onDone: () => void;
}) {
  const [planId, setPlanId] = useState(currentPlanId);
  const [period, setPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // BUG-11: when Autumn refuses the attach (502), the dialog flips into
  // a "retry-with-override" mode so the admin can either retry as-is
  // (e.g. transient Autumn error) or override Firestore-only with full
  // awareness of the desync risk.
  const [autumnError, setAutumnError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPlanId(currentPlanId);
      setPeriod('monthly');
      setReason('');
      setAutumnError(null);
    }
  }, [open, currentPlanId]);

  const doChangePlan = async (forceFirestoreOnly: boolean) => {
    if (!reason.trim() || planId === currentPlanId || busy) return;
    setBusy(true);
    try {
      const result = await adminApi.subscription.changePlan(
        companyId,
        planId,
        reason.trim(),
        { period, forceFirestoreOnly },
      );
      if (result.warning) {
        toast.warning(`Plan changed to ${planId} — ${result.warning}`);
      } else {
        toast.success(`Plan changed to ${planId} (${result.period})`);
      }
      onOpenChange(false);
      onDone();
    } catch (err) {
      const message = (err as Error).message;
      // BUG-11: detect the structured AUTUMN_ATTACH_FAILED 502 and
      // surface a retry/override UI rather than a plain toast.error.
      if (message.includes('AUTUMN_ATTACH_FAILED') || message.includes('Autumn refused')) {
        setAutumnError(message);
        toast.error('Autumn refused the plan change — choose retry or override.');
      } else {
        toast.error(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change plan</DialogTitle>
          <DialogDescription>
            Current plan: <span className="font-mono">{currentPlanId}</span>. The change is forced via Autumn `attach({'{ force: true }'})` so no checkout is required, but the next renewal could fail if the customer has no valid payment method.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cp-plan">New plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger id="cp-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-period">Billing period</Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as 'monthly' | 'annual')}>
              <SelectTrigger id="cp-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="cp-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
          {autumnError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
              <p className="font-semibold text-destructive">Autumn rejected the attach</p>
              <p className="mt-1 text-xs text-muted-foreground">{autumnError}</p>
              <p className="mt-2 text-xs">
                Choose <strong>Retry</strong> if this looks transient, or
                <strong> Force Firestore-only</strong> if you accept that recurring
                billing will keep using the previous plan until the customer
                re-attaches via Autumn.
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          {autumnError ? (
            <>
              <Button
                variant="outline"
                onClick={() => doChangePlan(false)}
                disabled={busy || !reason.trim() || planId === currentPlanId}
              >
                {busy ? 'Retrying...' : 'Retry'}
              </Button>
              <Button
                variant="destructive"
                onClick={() => doChangePlan(true)}
                disabled={busy || !reason.trim() || planId === currentPlanId}
              >
                {busy ? 'Forcing...' : 'Force Firestore-only'}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => doChangePlan(false)}
              disabled={busy || !reason.trim() || planId === currentPlanId}
            >
              {busy ? 'Applying...' : 'Apply'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelSubscriptionDialog({
  open, onOpenChange, companyId, onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  companyId: string;
  onDone: () => void;
}) {
  const [immediate, setImmediate] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setImmediate(false); setReason(''); } }, [open]);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await adminApi.subscription.cancel(companyId, immediate, reason.trim());
      toast.success(immediate ? 'Subscription canceled immediately' : 'Subscription will end at period end');
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-destructive" /> Cancel subscription?
          </AlertDialogTitle>
          <AlertDialogDescription>
            By default the cancellation takes effect at the end of the current billing period. Toggle "immediate" to cancel right now (causes a downgrade to freemium today).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch id="cancel-now" checked={immediate} onCheckedChange={setImmediate} />
            <Label htmlFor="cancel-now" className="cursor-pointer">Cancel immediately</Label>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Back</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || !reason.trim()}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {busy ? 'Cancelling...' : (immediate ? 'Cancel now' : 'Cancel at period end')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ResetCountersDialog({
  open, onOpenChange, companyId, onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  companyId: string;
  onDone: () => void;
}) {
  // v3.5: aiUser counter rimosso (pool eliminato).
  const [sel, setSel] = useState({ positions: false, candidates: false, seats: false, aiCompany: false, transcripts: false });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setSel({ positions: false, candidates: false, seats: false, aiCompany: false, transcripts: false }); setReason(''); } }, [open]);

  const checked = Object.entries(sel).filter(([, v]) => v).map(([k]) => k);
  const submit = async () => {
    if (!reason.trim() || checked.length === 0 || busy) return;
    setBusy(true);
    try {
      await adminApi.subscription.resetCounters(companyId, checked, reason.trim());
      toast.success(`Reset: ${checked.join(', ')}`);
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset counters</DialogTitle>
          <DialogDescription>
            Selectively zeroes the current period counters at <span className="font-mono">companies/{companyId}/billing/current_period</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(['positions', 'candidates', 'seats', 'aiCompany', 'transcripts'] as const).map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={sel[k]} onCheckedChange={(v) => setSel({ ...sel, [k]: v })} />
              <span>{k}</span>
            </label>
          ))}
          <div className="space-y-1.5 pt-2">
            <Label htmlFor="rc-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea id="rc-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || checked.length === 0 || !reason.trim()}>
            {busy ? 'Resetting...' : `Reset ${checked.length} counter${checked.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, destructive, onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="sub-reason-confirm">Reason <span className="text-destructive">*</span></Label>
          <Textarea id="sub-reason-confirm" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || !reason.trim()}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {busy ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
